<template>
  <div class="form-group" :data-testid="'field-' + field.key"
       :class="[to.inputType, {'formly-has-value': model[field.key], 'formly-has-focus': form[field.key].$active, 'has-error': hasError}]">

    <label v-if="to.label" v-text="$t('formly.fields.' + to.label)" :for="field.key"></label>
    <date-picker
      ref="datepicker"
      v-model="model[field.key]"
      :mode="to.mode ?? 'dateTime'"
      :locale="clientSettings?.lang"
      :minute-increment="5"
      :is24hr="clientSettings?.formatOfTime === '24h-clock'"
      :model-config="modelConfig"
      :masks="masks"
      :popover="{ visibility: 'none', keepVisibleOnInput: false, positionFixed: true }"
      @popoverDidShow="onPopoverShow"
      @popoverDidHide="onPopoverHide"
      @keydown.stop.prevent.native.esc="cancelSelection"
      @keydown.stop.prevent.native.enter="confirmSelection"
      @keydown.stop.prevent.capture.native.esc="cancelSelection"
      @keydown.stop.prevent.capture.native.enter="confirmSelection"
    >

      <template v-slot="{ inputValue, inputEvents, togglePopover }">
        <div class="datetime-picker">
          <button
            type="button"
            class="datetime-button"
            @click="onToggle(togglePopover)"
          >
            <i class="fa fa-calendar"></i>
          </button>
          <input
            id="date"
            class="datetime-input"
            :value="inputValue"
            v-on="filterEvents(inputEvents)"
            @focus="onFocus()"
          />
        </div>

      </template>

      <template v-slot:footer>
        <div class="bg-light p-2 text-center border-top rounded-bottom">
          <button
            type="button"
            class="btn btn-sm btn-danger mr-2"
            @click.stop="cancelSelection"
          >
            {{ $t('cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-success"
            @click.stop="confirmSelection"
          >
            {{ $t('confirm') }}
          </button>
        </div>
      </template>

    </date-picker>

    <span class="help-block form-text text-danger"
          v-if="dateErrorMessage"
          v-text="dateErrorMessage" />
  </div>
</template>

<script>
import { DatePicker } from 'v-calendar'
import baseField from '@/components/formly/formly-bootstrap/fields/baseField'

export default {
  components: { DatePicker },
  mixins: [baseField],
  data () {
    return {
      initialValue: null,
      isPopoverVisible: false
    }
  },
  computed: {
    clientSettings () {
      return this.$store.state.settings
    },
    dateErrorMessage () {
      const errors = this.form.$errors[this.field.key]
      if (!errors || !errors.length) return null
      const raw = errors[0]
      if (typeof raw !== 'string') return null
      // The backend sometimes sends a ready-made, human-readable message
      // (e.g. "The end date must be a date after start date.") instead of
      // a short rule code (e.g. "required"). Rule codes never contain
      // spaces, so show those messages as-is instead of trying to
      // translate them as `validation.<field>.<code>`.
      if (raw.includes(' ')) return raw
      return this.$t('validation.' + this.field.key + '.' + raw)
    },
    dateFormat () {
      const dateFormat = this.clientSettings?.formatOfDate

      let formattedDate
      switch (dateFormat) {
        case 'DMY':
          formattedDate = 'DD-MM-YYYY'
          break
        case 'YMD':
          formattedDate = 'YYYY-MM-DD'
          break
        case 'MDY':
          formattedDate = 'MM-DD-YYYY'
          break
        default:
          formattedDate = 'YYYY-MM-DD'
      }

      return formattedDate
    },
    timeFormat () {
      return this.clientSettings?.formatOfTime === '24h-clock' ? 'HH:mm' : 'hh:mm A'
    },
    dateTimeFormat () {
      return `${this.dateFormat} ${this.timeFormat}`
    },
    masks () {
      return {
        input: [this.dateTimeFormat],
        inputDateTime: [`${this.dateFormat} hh:mm A`],
        inputDateTime24hr: [`${this.dateFormat} HH:mm`],
        inputTime: ['hh:mm A'],
        inputTime24hr: ['HH:mm']
      }
    },
    modelConfig () {
      return {
        type: 'string',
        mask: 'YYYY-MM-DD HH:mm:ss'
      }
    }
  },
  methods: {
    filterEvents (events) {
      const filtered = { ...events }
      delete filtered.click
      delete filtered.focus
      return filtered
    },
    onToggle (togglePopover) {
      if (!this.isPopoverVisible) {
        this.initialValue = this.model[this.field.key]
      }
      togglePopover()
    },
    onFocus () {
      if (!this.isPopoverVisible) {
        this.initialValue = this.model[this.field.key]
      }
    },
    cancelSelection (event) {
      if (event) {
        if (typeof event.stopPropagation === 'function') event.stopPropagation()
        if (typeof event.preventDefault === 'function') event.preventDefault()
      }
      if (!this.isPopoverVisible) return

      this.model[this.field.key] = this.initialValue
      this.$refs.datepicker.hidePopover()
    },
    onPopoverHide () {
      this.isPopoverVisible = false
    },
    confirmSelection (event) {
      if (event) {
        if (typeof event.stopPropagation === 'function') event.stopPropagation()
        if (typeof event.preventDefault === 'function') event.preventDefault()
      }
      if (!this.isPopoverVisible) return

      this.$refs.datepicker.hidePopover()

      // Fokusiranje sledećeg polja u formi radi pristupačnosti
      this.$nextTick(() => {
        const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        const form = this.$el.closest('form')
        if (form) {
          // Pronalaženje svih fokusabilnih elemenata u formi
          const allElements = Array.from(form.querySelectorAll(focusableElements))
          // Filtriranje elemenata koji su unutar samog datepickera (kao što su dugmad u footeru)
          // i elemenata koji su skriveni
          const elements = allElements.filter(el => {
            const isInsideDatepicker = this.$el.querySelector('.datetime-picker').contains(el)
            const isVisible = !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
            const isButtonInFooter = el.closest('.bg-light.p-2.text-center.border-top')
            return (isInsideDatepicker || !this.$el.contains(el)) && isVisible && !isButtonInFooter
          })

          const index = elements.indexOf(this.$el.querySelector('.datetime-input'))
          if (index > -1 && elements[index + 1]) {
            elements[index + 1].focus()
          }
        }
      })
    },
    onPopoverShow () {
      this.isPopoverVisible = true
      this.$nextTick(() => {
        const date = this.model[this.field.key] || new Date()
        if (this.$refs.datepicker && typeof this.$refs.datepicker.focusDate === 'function') {
          this.$refs.datepicker.focusDate(date)
        }

        // Dodatni fokus na sam kalendar kako bi tastatura radila odmah
        const popover = this.$refs.datepicker?.$refs.popover
        if (popover && popover.$refs.content) {
          const container = popover.$refs.content.querySelector('.vc-container')
          if (container) {
            container.focus()
          }
        }
      })
    }
  }
}
</script>

<style>
.datetime-picker {
  display: flex;
  align-items: center;
  border-radius: 4px;
  overflow: hidden;
}

.datetime-button {
  background: #f8f9fa;
  border: none;
  border-right: 1px solid #ced4da;
  padding: 6px 12px;
  cursor: pointer;
  outline: none;
  transition: background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.datetime-button:hover {
  background-color: #e2e6ea;
}

.datetime-button:focus {
  background-color: #e2e6ea;
  box-shadow: inset 0 0 0 2px #80bdff;
  z-index: 1;
}

.datetime-input {
  border: none;
  padding: 6px 12px;
  flex-grow: 1;
  outline: none;
}

.vc-popover-content-wrapper {
  z-index: 9999 !important;
}

@media (max-height: 480px), (max-width: 480px) {
  .vc-popover-content-wrapper {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    margin: 0 !important;
  }
  .vc-popover-content {
    max-height: 95vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  /* Uklanjanje strelice popovera na mobilnim uređajima jer ga centriramo */
  .vc-popover-caret {
    display: none !important;
  }
}
</style>
